/* =====================================================================================================================
   MAKERZ · PDP scripts
   <mz-sticky-atc>: mobile sticky bar that scrolls to the purchase area instead of adding to cart.
   ===================================================================================================================== */

const MOBILE_QUERY = '(max-width: 999px)';

const TARGET_SELECTORS = {
  quantity: '.product-info > [data-block-type="@app"], .product-info__block-group > [data-block-type="@app"]',
  variant_picker: '[data-block-type="variant-picker"]',
  buy_buttons: '[data-block-type="buy-buttons"]',
};

/* --sticky-area-height is a calc() on :root, so it cannot be parsed from getComputedStyle. Measure it instead. */
function stickyAreaHeight() {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;height:var(--sticky-area-height, 0px);';
  document.body.appendChild(probe);
  const height = probe.offsetHeight;
  probe.remove();
  return height;
}

class MzStickyAtc extends HTMLElement {
  #abort = null;
  #frame = null;
  #visible = false;
  #mql = window.matchMedia(MOBILE_QUERY);

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.querySelector('button')?.addEventListener('click', this.#scrollToTarget.bind(this), { signal });
    window.addEventListener('scroll', this.#schedule.bind(this), { passive: true, signal });
    window.addEventListener('resize', this.#schedule.bind(this), { passive: true, signal });
    this.#mql.addEventListener('change', this.#schedule.bind(this), { signal });
    document.addEventListener('variant:change', this.#onVariantChange.bind(this), { signal });

    this.#schedule();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  get #section() {
    return document.getElementById(`shopify-section-${this.getAttribute('section-id')}`);
  }

  get #buyButtons() {
    return this.#section?.querySelector(TARGET_SELECTORS.buy_buttons) ?? null;
  }

  get #target() {
    const section = this.#section;
    if (!section) return null;

    const selector = TARGET_SELECTORS[this.getAttribute('target')] ?? TARGET_SELECTORS.quantity;
    // App blocks can be empty (e.g. no active gift campaign): take the first one that actually renders something
    const match = Array.from(section.querySelectorAll(selector)).find((el) => el.offsetHeight > 0);

    return match ?? this.#buyButtons;
  }

  #schedule() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#update();
    });
  }

  #update() {
    if (!this.#mql.matches) return this.#setVisible(false);

    const showAfter = parseInt(this.getAttribute('show-after'), 10) || 0;
    if (window.scrollY < showAfter) return this.#setVisible(false);

    // Hide while any part of the purchase area (target → add to cart button) is on screen
    const buyButtons = this.#buyButtons;
    const target = this.#target;
    if (!buyButtons) return this.#setVisible(true);

    const buyRect = buyButtons.getBoundingClientRect();
    const top = Math.min(target ? target.getBoundingClientRect().top : buyRect.top, buyRect.top);
    const bottom = buyRect.bottom;
    const viewportBottom = window.innerHeight - (this.#visible ? this.offsetHeight : 0);
    const purchaseAreaOnScreen = bottom > stickyAreaHeight() && top < viewportBottom;

    this.#setVisible(!purchaseAreaOnScreen);
  }

  #setVisible(visible) {
    if (visible === this.#visible) return;
    this.#visible = visible;

    this.classList.toggle('is-visible', visible);
    this.setAttribute('aria-hidden', String(!visible));
    this.toggleAttribute('inert', !visible);
  }

  #scrollToTarget() {
    const target = this.#target;
    if (!target) return;

    const offset = parseInt(this.getAttribute('scroll-offset'), 10) || 0;
    const top = target.getBoundingClientRect().top + window.scrollY - stickyAreaHeight() - offset;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({ top: Math.max(top, 0), behavior: reduceMotion ? 'auto' : 'smooth' });

    // Move focus with the scroll so keyboard and screen reader users land on the purchase area
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.classList.add('mz-focus-target');
    target.focus({ preventScroll: true });
  }

  #onVariantChange(event) {
    if (event.detail?.formId !== this.getAttribute('form')) return;

    const label = this.querySelector('.mz-sticky-atc__label');
    if (label) {
      label.textContent = event.detail.variant?.available ? this.getAttribute('label-available') : this.getAttribute('label-sold-out');
    }

    // The theme re-renders the buy buttons block on variant change: re-measure against the new element
    this.#schedule();
  }
}

if (!window.customElements.get('mz-sticky-atc')) {
  window.customElements.define('mz-sticky-atc', MzStickyAtc);
}